import { memo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Project } from "@/services/project.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import type { Columns, UiTask } from "./types";
import { DraggableTaskCard } from "./TaskCard";
import { CollapseStageButton, CollapsedStageCircle } from "./StageCollapse";

// Flat (ungrouped) Kanban column: one droppable per stage, tasks listed
// directly. Droppable id is the bare stage key.
function BoardColumn({
	stage,
	tasks,
	projectsById,
	onDelete,
	onView,
	onAdd,
	collapsed,
	onToggleCollapse,
}: {
	stage: WorkflowStage;
	tasks: UiTask[];
	projectsById: Map<string, Project>;
	onDelete: (task: UiTask) => void;
	onView: (task: UiTask) => void;
	onAdd?: (stageKey: string, projectId?: string) => void;
	collapsed: boolean;
	onToggleCollapse: (stageKey: string) => void;
}) {
	const { setNodeRef, isOver } = useDroppable({ id: stage.key });

	// Collapsed: narrow strip, still a drop target.
	if (collapsed) {
		return (
			<div className="flex flex-col items-center w-12 shrink-0 h-[-webkit-fill-available] snap-start">
				<div className="mb-3">
					<CollapsedStageCircle
						stage={stage}
						count={tasks.length}
						onExpand={() => onToggleCollapse(stage.key)}
					/>
				</div>
				<div
					ref={setNodeRef}
					className={cn(
						"w-full flex-1 rounded-xl min-h-[80vh] transition-colors",
						isOver
							? "bg-primary-subtle/60 border border-dashed border-primary/40"
							: "bg-muted-subtle",
					)}
				/>
			</div>
		);
	}

	return (
		<div className="flex flex-col min-w-[300px] h-[-webkit-fill-available] snap-start">
			<div className="flex items-center gap-2 mb-3 px-1">
				<span
					className="h-2 w-2 rounded-full"
					style={{ backgroundColor: stage.color }}
				/>
				<span className="text-sm font-semibold text-foreground">
					{stage.name}
				</span>
				<span className="ml-auto text-xs font-medium text-muted bg-muted-subtle px-2 py-0.5 rounded-full">
					{tasks.length}
				</span>
				<CollapseStageButton
					stage={stage}
					onClick={() => onToggleCollapse(stage.key)}
				/>
			</div>

			<div
				ref={setNodeRef}
				className={cn(
					"group/cell flex-1 flex flex-col gap-2.5 rounded-xl p-2 min-h-[80vh] transition-colors h-full",
					isOver
						? "bg-primary-subtle/60 border border-dashed border-primary/40"
						: "bg-muted-subtle",
				)}
			>
				{tasks.map((task) => (
					<DraggableTaskCard
						key={task.id}
						task={task}
						projectsById={projectsById}
						onDelete={onDelete}
						onView={onView}
					/>
				))}

				{onAdd && !isOver && (
					<button
						type="button"
						onClick={() => onAdd(stage.key)}
						aria-label={`Add task to ${stage.name}`}
						className="flex-1 min-h-10 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-transparent text-xs text-muted opacity-0 transition-opacity group-hover/cell:opacity-100 focus-visible:opacity-100 hover:border-border hover:bg-surface/60 hover:text-foreground"
					>
						<Plus className="h-3.5 w-3.5" /> Add task
					</button>
				)}

				{!onAdd && tasks.length === 0 && (
					<div className="flex-1 flex items-center justify-center py-8">
						<p className="text-xs text-muted">Drop tasks here</p>
					</div>
				)}
			</div>
		</div>
	);
}

function ColumnBoardBase({
	stages,
	filteredColumns,
	projectsById,
	onDelete,
	onView,
	onAdd,
	collapsedStages,
	onToggleStage,
	boardRef,
}: {
	stages: WorkflowStage[];
	filteredColumns: Columns;
	projectsById: Map<string, Project>;
	onDelete: (task: UiTask) => void;
	onView: (task: UiTask) => void;
	onAdd?: (stageKey: string, projectId?: string) => void;
	collapsedStages: Record<string, boolean>;
	onToggleStage: (stageKey: string) => void;
	boardRef: React.Ref<HTMLDivElement>;
}) {
	return (
		<div
			ref={boardRef}
			className="flex gap-4 overflow-x-auto pb-4 items-start snap-x snap-mandatory"
		>
			{stages.map((stage) => (
				<BoardColumn
					key={stage.key}
					stage={stage}
					tasks={filteredColumns[stage.key] ?? []}
					projectsById={projectsById}
					onDelete={onDelete}
					onView={onView}
					onAdd={onAdd}
					collapsed={!!collapsedStages[stage.key]}
					onToggleCollapse={onToggleStage}
				/>
			))}
		</div>
	);
}

export const ColumnBoard = memo(ColumnBoardBase);
